import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { ChevronRightIcon } from '@heroicons/react/24/outline'

import {
  DETAILS_FILTERS_KEY,
  LIST_FILTERS_KEY,
} from '@/containers/billing/register/billingFilters/hooks/useBillingFilters'
import {
  BILLING_STORAGE_KEYS,
  INITIAL_PAGE,
} from '@/containers/billing/register/billingTable/TableContext'
import { CASH_OPERATION_STORAGE_KEYS } from '@/containers/cashOperations/table/TableContext'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { storage } from '@/lib/utils'

import useServerBusiness from '../../api/useServerBusiness'
import { changeBusiness } from '../../store/actions/globals'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import ImageComponent from '../misc/Images/Image'
import SearchComponent from '../misc/SearchComponent'
import SpinnerLoading from '../misc/SpinnerLoading'

interface BranchList {
  closeModal: () => void
}

export default function BranchesList({ closeModal }: BranchList) {
  const queryClient = useQueryClient()
  const { refetchBusinessData } = useBusinessDataQuery({})
  const { business, branches, user } = useAppSelector((state) => state.init)
  const dispatch = useAppDispatch()
  const index = branches.findIndex((item) => item.isMain)
  const redirect = useNavigate()
  const { getAllBusiness, allBusiness, isLoading } = useServerBusiness()
  const [filter, setFilter] = useState<Record<string, string>>({})
  const changeBranch = (id: number | null) => {
    dispatch(changeBusiness(id))
    refetchBusinessData()
    // Invalidate cache for list, when business changes
    // to ensure the correct business data is displayed
    queryClient.invalidateQueries({
      queryKey: ['dispatches'],
    })
    queryClient.invalidateQueries({
      queryKey: ['containers'],
    })
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) && q.queryKey[0] === 'containers-products',
    })
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) && q.queryKey.includes('inventory-details'),
    })
    queryClient.invalidateQueries({
      queryKey: ['container_batches'],
    })
    queryClient.invalidateQueries({
      queryKey: ['movements'],
    })

    storage.set(BILLING_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    storage.set(CASH_OPERATION_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    storage.set(LIST_FILTERS_KEY, {})
    storage.set(DETAILS_FILTERS_KEY, {})
    redirect('/')
    closeModal()
  }

  useEffect(() => {
    if (Object.keys(filter).length !== 0) {
      getAllBusiness(filter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return (
    <div className="h-[28rem]">
      <div className="cursor-pointer border-b-2 border-gray-200 bg-white pb-4">
        <div className="-ml-4 -mt-4 flex items-center">
          <div className="ml-4 mt-4 flex flex-grow items-center justify-between">
            <div
              className="flex items-center"
              onClick={() => changeBranch(branches[index]?.id ?? null)}
            >
              <div className="flex-shrink-0">
                <ImageComponent
                  className="h-12 w-12 overflow-hidden rounded-full"
                  hash={branches[index].logo?.blurHash}
                  src={branches[index].logo?.src}
                />
              </div>
              <div className="ml-4">
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  {branches[index]?.name}
                </h3>
              </div>
            </div>
            {business?.id === branches[index].id && (
              <span className="mr-6 h-2 w-2 rounded-full bg-green-500" />
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 flow-root h-full max-h-[22rem] overflow-auto scrollbar-none">
        <ul className="-my-5 divide-y divide-gray-200 py-2" role="list">
          {user?.isSuperAdmin ? (
            <div className="relative mt-2 flex flex-col gap-2 p-1">
              <div className="sticky top-0 pb-2">
                <SearchComponent
                  findAction={(value: string | null) =>
                    setFilter(value ? { search: value } : {})
                  }
                  placeholder="Buscar negocio"
                />
              </div>
              {isLoading ? (
                <div className="flex h-full items-center justify-center">
                  <SpinnerLoading />
                </div>
              ) : (
                allBusiness
                  .filter((item) => item.id !== branches[index].id)
                  .map((branch) => (
                    <li className="cursor-pointer py-4" key={branch.id}>
                      <div
                        className="flex items-center space-x-4"
                        onClick={() => changeBranch(branch.id ?? null)}
                      >
                        <div className="flex-shrink-0">
                          <ImageComponent
                            className="h-8 w-8 overflow-hidden rounded-full"
                            hash={branch.logo?.blurHash}
                            src={branch.logo?.src}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {branch.name}
                          </p>
                        </div>
                        <div className="inline-flex items-center gap-2">
                          {business?.id === branch.id && (
                            <span className="h-2 w-2 rounded-full bg-green-400"></span>
                          )}
                          <span>
                            <ChevronRightIcon className="h-4" />
                          </span>
                        </div>
                      </div>
                    </li>
                  ))
              )}
            </div>
          ) : (
            branches
              .filter((item) => !item.isMain)
              .map((branch) => (
                <li className="cursor-pointer py-4" key={branch.id}>
                  <div
                    className="flex items-center space-x-4"
                    onClick={() => changeBranch(branch.id ?? null)}
                  >
                    <div className="flex-shrink-0">
                      <ImageComponent
                        className="h-8 w-8 overflow-hidden rounded-full"
                        hash={branch.logo?.blurHash}
                        src={branch.logo?.src}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {branch.name}
                      </p>
                    </div>
                    <div className="inline-flex items-center gap-2">
                      {business?.id === branch.id && (
                        <span className="h-2 w-2 rounded-full bg-green-400"></span>
                      )}
                      <span>
                        <ChevronRightIcon className="h-4" />
                      </span>
                    </div>
                  </div>
                </li>
              ))
          )}
        </ul>
      </div>
    </div>
  )
}
